import { z } from 'zod';
/**
 * Questionários (N1, estudo do nutricionista): a clínica manda o link antes da consulta e o
 * paciente responde sem entrar em conta (ou pela Saúde). As perguntas são dado: cada nicho
 * tem os seus modelos. As respostas ficam cifradas e entram na ficha e na linha do tempo.
 */
const isoDateTime = z.iso.datetime();
export const FORM_QUESTION_KINDS = [
    'texto',
    'longo',
    'numero',
    'uma',
    'varias',
    'escala',
    'simnao',
];
const MEALS = ['Café da manhã', 'Lanche da manhã', 'Almoço', 'Lanche da tarde', 'Jantar', 'Ceia'];
export const CLINIC_FORM_TEMPLATES = [
    {
        key: 'pre_nutricao',
        title: 'Pré-consulta de nutrição',
        intro: 'Estas perguntas ajudam a aproveitar melhor a consulta. Responda com calma; as marcadas "se quiser" podem ficar em branco.',
        minutes: 8,
        for: ['nutricao', 'emagrecimento'],
        sections: [
            {
                title: 'Seu objetivo',
                questions: [
                    {
                        key: 'objetivo',
                        label: 'O que você quer alcançar com o acompanhamento?',
                        kind: 'varias',
                        options: [
                            'Perder peso',
                            'Ganhar massa muscular',
                            'Melhorar a saúde e os exames',
                            'Ter mais energia e disposição',
                            'Melhorar a alimentação da família',
                            'Acompanhar uma condição de saúde',
                            'Desempenho no esporte',
                        ],
                    },
                    {
                        key: 'objetivo_texto',
                        label: 'Conte com suas palavras',
                        kind: 'longo',
                        optional: true,
                    },
                ],
            },
            {
                title: 'Hoje',
                questions: [
                    {
                        key: 'peso',
                        label: 'Peso atual (aproximado)',
                        kind: 'numero',
                        unit: 'kg',
                        min: 20,
                        max: 400,
                        optional: true,
                    },
                    {
                        key: 'altura',
                        label: 'Altura',
                        kind: 'numero',
                        unit: 'cm',
                        min: 80,
                        max: 250,
                        optional: true,
                    },
                    {
                        key: 'refeicoes',
                        label: 'Quantas refeições você faz por dia?',
                        kind: 'numero',
                        unit: 'refeições',
                        min: 1,
                        max: 12,
                    },
                    {
                        key: 'cozinha',
                        label: 'Quem prepara a sua comida na maior parte das vezes?',
                        kind: 'uma',
                        options: [
                            'Eu',
                            'Alguém da família',
                            'Restaurante ou marmita',
                            'Delivery',
                            'Varia muito',
                        ],
                    },
                    {
                        key: 'agua',
                        label: 'Quantos copos de água você bebe por dia?',
                        kind: 'numero',
                        unit: 'copos',
                        min: 0,
                        max: 40,
                    },
                    {
                        key: 'intestino',
                        label: 'Seu intestino funciona',
                        kind: 'uma',
                        options: [
                            'Todo dia',
                            'Dia sim, dia não',
                            'Duas vezes por semana ou menos',
                            'Varia muito',
                        ],
                    },
                    {
                        key: 'sono',
                        label: 'Quantas horas você dorme por noite?',
                        kind: 'numero',
                        unit: 'horas',
                        min: 0,
                        max: 16,
                    },
                    {
                        key: 'fome',
                        label: 'De 0 a 10, quanto a ansiedade mexe com a sua fome?',
                        kind: 'escala',
                        min: 0,
                        max: 10,
                    },
                ],
            },
            {
                title: 'Saúde',
                questions: [
                    {
                        key: 'doencas',
                        label: 'Tem alguma doença ou condição de saúde?',
                        kind: 'simnao',
                        detail: 'Quais?',
                    },
                    {
                        key: 'remedios',
                        label: 'Usa algum remédio todo dia?',
                        kind: 'simnao',
                        detail: 'Quais e a dose?',
                    },
                    {
                        key: 'suplementos',
                        label: 'Usa suplemento ou vitamina?',
                        kind: 'simnao',
                        detail: 'Quais?',
                    },
                    {
                        key: 'alergias',
                        label: 'Tem alergia ou intolerância a algum alimento?',
                        kind: 'simnao',
                        detail: 'A quais?',
                    },
                    {
                        key: 'cirurgias',
                        label: 'Já fez cirurgia (inclusive bariátrica)?',
                        kind: 'simnao',
                        detail: 'Qual e quando?',
                    },
                    {
                        key: 'exames',
                        label: 'Fez exames de sangue nos últimos 6 meses?',
                        kind: 'simnao',
                        detail: 'Se puder, leve na consulta.',
                    },
                ],
            },
            {
                title: 'Hábitos',
                questions: [
                    {
                        key: 'atividade',
                        label: 'Faz atividade física?',
                        kind: 'simnao',
                        detail: 'Qual, quantas vezes por semana e por quanto tempo?',
                    },
                    {
                        key: 'alcool',
                        label: 'Bebe bebida alcoólica?',
                        kind: 'uma',
                        options: ['Não', 'Raramente', 'Fins de semana', 'Quase todo dia'],
                    },
                    { key: 'fuma', label: 'Fuma?', kind: 'uma', options: ['Não', 'Sim', 'Parei'] },
                    {
                        key: 'dietas',
                        label: 'Já fez dieta antes?',
                        kind: 'simnao',
                        detail: 'Como foi? O que funcionou e o que não funcionou?',
                    },
                ],
            },
            {
                title: 'Do que você gosta',
                questions: [
                    { key: 'gosta', label: 'Alimentos que você gosta muito', kind: 'longo', optional: true },
                    {
                        key: 'nao_gosta',
                        label: 'Alimentos que você não come de jeito nenhum',
                        kind: 'longo',
                        optional: true,
                    },
                ],
            },
            {
                title: 'Um dia comum',
                questions: MEALS.map((meal, index) => ({
                    key: `dia_${index + 1}`,
                    label: meal,
                    kind: 'longo',
                    ...(index === 0
                        ? {
                            help: 'O que e quanto você costuma comer, e o horário. Ex.: 7h, 2 pães com manteiga e café com leite.',
                        }
                        : {}),
                    optional: true,
                })),
            },
        ],
    },
    {
        key: 'anamnese_treino',
        title: 'Anamnese para o treino',
        short: 'Anamnese',
        summary: 'objetivo, saúde, triagem para o exercício (lógica do ACSM) e o termo de consentimento, respondidos antes da primeira sessão',
        intro: 'Antes do primeiro treino, conte um pouco de você. Com isso o seu personal monta um treino seguro para o seu momento. Leva uns 6 minutos.',
        minutes: 6,
        for: ['educacao_fisica', 'treino'],
        sections: [
            {
                title: 'Seu objetivo',
                questions: [
                    {
                        key: 'objetivo',
                        label: 'O que você quer com o treino?',
                        kind: 'varias',
                        options: [
                            'Ganhar massa muscular',
                            'Emagrecer',
                            'Condicionamento',
                            'Força',
                            'Saúde e disposição',
                            'Voltar depois de lesão',
                            'Mobilidade e menos dores',
                            'Desempenho num esporte',
                        ],
                    },
                    {
                        key: 'objetivo_texto',
                        label: 'Quer contar mais? Um prazo, um evento, algo que já tentou',
                        kind: 'longo',
                        optional: true,
                    },
                ],
            },
            {
                title: 'Triagem para o exercício',
                questions: [
                    {
                        key: 'ativo',
                        label: 'Nos últimos 3 meses, você fez exercício planejado pelo menos 3 vezes por semana, uns 30 minutos, num ritmo que acelera a respiração?',
                        kind: 'simnao',
                    },
                    {
                        key: 'doenca',
                        label: 'Algum médico já disse que você tem doença do coração, das artérias, já teve AVC, tem diabetes ou doença nos rins?',
                        kind: 'simnao',
                        detail: 'Qual?',
                    },
                    {
                        key: 'sintomas',
                        label: 'Você sente algum destes?',
                        kind: 'varias',
                        none: 'Nenhum desses',
                        options: [
                            'Dor ou aperto no peito, pescoço, queixo ou braços',
                            'Falta de ar em repouso ou com pouco esforço',
                            'Tontura ou desmaio',
                            'Falta de ar ao deitar ou acordar sem ar à noite',
                            'Inchaço nos tornozelos',
                            'Coração disparado ou batendo fora do ritmo',
                            'Dor na perna ao caminhar, que passa ao parar',
                            'Sopro no coração',
                            'Cansaço fora do normal no dia a dia',
                            'Nenhum desses',
                        ],
                    },
                    {
                        key: 'intensidade',
                        label: 'Como você imagina o treino?',
                        kind: 'uma',
                        options: ['Leve', 'Moderado', 'Puxado'],
                    },
                ],
            },
            {
                title: 'Saúde',
                questions: [
                    { key: 'pressao', label: 'Tem pressão alta?', kind: 'simnao', detail: 'Toma remédio?' },
                    {
                        key: 'dores',
                        label: 'Tem alguma dor, lesão ou limitação (coluna, joelho, ombro…)?',
                        kind: 'simnao',
                        detail: 'Onde e desde quando?',
                    },
                    {
                        key: 'cirurgias',
                        label: 'Fez alguma cirurgia nos últimos 12 meses?',
                        kind: 'simnao',
                        detail: 'Qual?',
                    },
                    {
                        key: 'remedios',
                        label: 'Toma algum remédio todo dia?',
                        kind: 'simnao',
                        detail: 'Quais?',
                    },
                    {
                        key: 'gestante',
                        label: 'Está grávida ou teve bebê nos últimos 6 meses?',
                        kind: 'uma',
                        options: ['Não', 'Sim', 'Não se aplica'],
                    },
                    { key: 'fuma', label: 'Fuma?', kind: 'uma', options: ['Não', 'Sim', 'Parei'] },
                    {
                        key: 'sono',
                        label: 'Quantas horas você dorme por noite?',
                        kind: 'numero',
                        unit: 'horas',
                        min: 2,
                        max: 14,
                    },
                ],
            },
            {
                title: 'Sua rotina',
                questions: [
                    {
                        key: 'dias',
                        label: 'Quantos dias por semana dá para treinar?',
                        kind: 'numero',
                        unit: 'dias',
                        min: 1,
                        max: 7,
                    },
                    {
                        key: 'tempo',
                        label: 'Quanto tempo por treino?',
                        kind: 'uma',
                        options: ['30 minutos', '45 minutos', '1 hora', 'Mais de 1 hora'],
                    },
                    {
                        key: 'local',
                        label: 'Onde você vai treinar?',
                        kind: 'uma',
                        options: ['Academia', 'Estúdio', 'Em casa', 'Ao ar livre'],
                    },
                    {
                        key: 'historico',
                        label: 'Já treinou antes? Do que gostou e do que não gostou',
                        kind: 'longo',
                        optional: true,
                    },
                ],
            },
            {
                title: 'Termo de consentimento',
                questions: [
                    {
                        key: 'termo',
                        label: 'Li e concordo com o termo de consentimento',
                        kind: 'simnao',
                        mustBeYes: true,
                        help: 'Concordo com o acompanhamento pelo meu personal, presencial ou a distância pelo aplicativo. Respondi com verdade e aviso se algo mudar na minha saúde. Sei que exercício tem riscos: paro e aviso se sentir dor no peito, tontura ou falta de ar fora do normal, e procuro liberação médica quando a triagem indicar. Autorizo guardar estas respostas, as avaliações e os treinos que eu registrar, com sigilo, só para o meu acompanhamento; posso pedir uma cópia quando quiser.',
                    },
                ],
            },
        ],
    },
];
export function formTemplate(key) {
    return CLINIC_FORM_TEMPLATES.find((row) => row.key === key) ?? null;
}
/** Os modelos que servem a uma clínica (pela especialidade e pelos serviços). */
export function formTemplatesFor(specialty, services) {
    const tags = new Set([specialty, ...services]);
    return CLINIC_FORM_TEMPLATES.filter((row) => row.for.some((tag) => tags.has(tag)));
}
/** Uma resposta: texto, número, lista de opções ou sim/não com o detalhe. */
export const formAnswerSchema = z.union([
    z.string().max(2000),
    z.number(),
    z.array(z.string().max(120)).max(20),
    z.object({ yes: z.boolean(), detail: z.string().max(1000).optional() }),
]);
export const formAnswersSchema = z.record(z.string().max(40), formAnswerSchema);
/**
 * Confere as respostas contra o modelo: chave que não existe sai, tipo errado sai, número
 * fora da faixa sai. Devolve as respostas limpas e o que faltou responder (obrigatórias).
 */
export function cleanFormAnswers(template, answers) {
    const out = {};
    const missing = [];
    for (const section of template.sections)
        for (const q of section.questions) {
            const value = answers[q.key];
            let ok;
            if (q.kind === 'texto' || q.kind === 'longo') {
                if (typeof value === 'string' && value.trim())
                    ok = value.trim().slice(0, q.kind === 'texto' ? 200 : 2000);
            }
            else if (q.kind === 'numero' || q.kind === 'escala') {
                if (typeof value === 'number' &&
                    Number.isFinite(value) &&
                    (q.min === undefined || value >= q.min) &&
                    (q.max === undefined || value <= q.max))
                    ok = value;
            }
            else if (q.kind === 'uma') {
                if (typeof value === 'string' && q.options?.includes(value))
                    ok = value;
            }
            else if (q.kind === 'varias') {
                if (Array.isArray(value)) {
                    let picked = [...new Set(value.filter((item) => q.options?.includes(item)))];
                    // "Nenhum desses" junto de outra opção: vale o que foi marcado.
                    if (q.none && picked.length > 1)
                        picked = picked.filter((item) => item !== q.none);
                    if (picked.length)
                        ok = picked;
                }
            }
            else if (q.kind === 'simnao') {
                if (value &&
                    typeof value === 'object' &&
                    !Array.isArray(value) &&
                    typeof value.yes === 'boolean')
                    ok = {
                        yes: value.yes,
                        ...(value.yes && value.detail?.trim()
                            ? { detail: value.detail.trim().slice(0, 1000) }
                            : {}),
                    };
            }
            if (ok !== undefined)
                out[q.key] = ok;
            if ((ok === undefined && !q.optional) || (q.mustBeYes && !ok?.yes))
                missing.push(q.label);
        }
    return { answers: out, missing };
}
/** A resposta em texto, para a ficha e para o PDF. */
export function formAnswerText(q, value) {
    if (value === undefined)
        return 'Não respondeu';
    if (typeof value === 'number')
        return q.unit ? `${value.toLocaleString('pt-BR')} ${q.unit}` : value.toLocaleString('pt-BR');
    if (typeof value === 'string')
        return value;
    if (Array.isArray(value))
        return value.join(', ');
    return value.yes ? `Sim${value.detail ? `: ${value.detail}` : ''}` : 'Não';
}
export const CLINIC_FORM_STATUSES = ['enviado', 'respondido', 'expirado'];
export const clinicFormSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    templateKey: z.string(),
    title: z.string(),
    status: z.enum(CLINIC_FORM_STATUSES),
    sentAt: isoDateTime,
    answeredAt: isoDateTime.nullable(),
    expiresAt: isoDateTime,
    sentBy: z.string(),
    /** Só para quem tem o dado clínico, e só depois de respondido. */
    answers: formAnswersSchema.nullable(),
});
export const sendClinicFormSchema = z.object({ templateKey: z.string().min(1).max(40) });
export const clinicFormLinkSchema = z.object({
    form: clinicFormSchema,
    url: z.url(),
    /** O texto pronto para o WhatsApp. */
    message: z.string(),
});
/** O que a página pública recebe: o modelo e quem pergunta, nunca dado do paciente além do primeiro nome. */
export const publicClinicFormSchema = z.object({
    templateKey: z.string(),
    clinicName: z.string(),
    firstName: z.string(),
    status: z.enum(CLINIC_FORM_STATUSES),
    logoVersion: z.number().nullable(),
    clinicId: z.uuid(),
});
export const answerClinicFormSchema = z.object({ answers: formAnswersSchema });
/**
 * Triagem de pré-participação pela lógica do ACSM (Riebe et al., 2015): quem já se exercita,
 * se tem doença do coração, metabólica ou renal conhecida, e se tem sinais ou sintomas. É
 * orientação ao profissional, não diagnóstico: a decisão e a conversa são dele.
 */
export function trainingScreening(answers) {
    const yesNo = (key) => {
        const value = answers[key];
        return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    };
    const active = yesNo('ativo')?.yes;
    const disease = yesNo('doenca')?.yes;
    const raw = answers.sintomas;
    if (active === undefined || disease === undefined || !Array.isArray(raw))
        return null;
    const symptoms = raw.filter((item) => item !== 'Nenhum desses');
    const vigorous = answers.intensidade === 'Puxado';
    const detail = (key) => {
        const text = yesNo(key)?.detail;
        return text ? `: ${text}` : '';
    };
    const notes = [];
    if (disease)
        notes.push(`Doença conhecida${detail('doenca')}`);
    if (symptoms.length)
        notes.push(`Sinais ou sintomas: ${symptoms.join('; ')}`);
    if (yesNo('pressao')?.yes)
        notes.push(`Pressão alta${detail('pressao')}`);
    if (yesNo('dores')?.yes)
        notes.push(`Dor ou lesão${detail('dores')}`);
    if (yesNo('cirurgias')?.yes)
        notes.push(`Cirurgia recente${detail('cirurgias')}`);
    if (yesNo('remedios')?.yes)
        notes.push(`Remédio de uso diário${detail('remedios')}`);
    if (answers.gestante === 'Sim')
        notes.push('Gestante ou pós-parto recente');
    if (answers.fuma === 'Sim')
        notes.push('Fuma');
    let result;
    if (symptoms.length && active)
        result = {
            level: 'medico',
            title: 'Parar e pedir liberação médica',
            text: 'Já treina e relata sinais ou sintomas: interromper o exercício até a liberação médica.',
        };
    else if (symptoms.length)
        result = {
            level: 'medico',
            title: 'Liberação médica antes de começar',
            text: 'Relata sinais ou sintomas: começar só depois da liberação médica.',
        };
    else if (disease && !active)
        result = {
            level: 'medico',
            title: 'Liberação médica antes de começar',
            text: 'Doença conhecida e sem exercício regular: liberação médica antes de começar.',
        };
    else if (disease)
        result = {
            level: 'atencao',
            title: vigorous ? 'Moderado liberado; médico antes do puxado' : 'Pode seguir no moderado',
            text: 'Doença conhecida, sem sintomas e já treina: moderado sem liberação; para o vigoroso, liberação médica antes.',
        };
    else if (!active)
        result = {
            level: 'ok',
            title: 'Pode começar, do leve ao moderado',
            text: 'Sem doença conhecida nem sintomas: começar leve a moderado e progredir aos poucos.',
        };
    else
        result = {
            level: 'ok',
            title: 'Pode seguir treinando',
            text: 'Sem doença conhecida nem sintomas e já treina: seguir no moderado ou no vigoroso, progredindo.',
        };
    // A triagem libera, mas há o que olhar (pressão, lesão, gestação): fica em atenção.
    if (result.level === 'ok' && notes.length)
        result = { ...result, level: 'atencao' };
    return { ...result, notes };
}
